import React, { useState } from "react";
import { useSelector } from "react-redux"; // Redux importieren
import { useTranslation } from "next-i18next";

import IconThumbUp from "@/assets/icons/iconThumbUp.svg";
import IconThumbDown from "@/assets/icons/iconThumbDown.svg";
import IconXmark from "@/assets/icons/iconXmark.svg";
import {
  QUALITY_MAP,
  PAPER_STORAGE_LOOKUP,
  getPaperStorageRatingKey,
} from "@/utils/qualityConfig";

// 📌 Bewertung für Papierwechsel & Druckzeit
const getPaperStorageSizeRating = (volume, speed = 0, guestCount = 50, t) => {
  if (volume === 0) {
    return formatRating(
      "rating.NoInfo",
      t("rating.NoPrinterInfo"),
      IconXmark,
      "text-[#B65237]",
      t,
    );
  }

  const ratingKey = getPaperStorageRatingKey(volume, guestCount);

  return formatRating(
    ratingKey,
    t("printerDetails", {
      rating: t(ratingKey),
      guestCount: guestCount === 201 ? t("moreThan200") : guestCount,
      volume,
      speed: speed || "?",
    }),
    IconThumbUp,
    "text-[#6FA68F]",
    t,
  );
};

// 📌 Formatierung der Bewertung
const formatRating = (
  rating,
  details,
  icon = IconThumbUp,
  colorClass = "text-[#6FA68F]",
  t,
) => {
  if (rating === "rating.NoInfo") {
    icon = IconXmark;
    colorClass = "text-[#B65237]";
  } else if (rating === "rating.NotIdeal") {
    icon = IconThumbDown;
    colorClass = "text-[#B65237]";
  } else if (rating === "rating.Okay") {
    icon = IconThumbUp;
    colorClass = "text-[#FF9900]";
  }

  return {
    rating: t(rating),
    colorClass,
    icon,
    details,
    t,
  };
};

// Tooltip-Komponente für Hover-Info
const Tooltip = ({ text }) => (
  <div className="absolute z-[9999] bg-black text-white text-xs rounded-md p-1 px-2 shadow-lg top-full mt-1 left-1/2 transform -translate-x-1/2 whitespace-pre break-normal">
    {text}
  </div>
);

function ProductListElementQuality({ item = "no", type, printer }) {
  const { t } = useTranslation("common");
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const guestCount = useSelector((state) => state.filters.guestCount); // Redux `guestCount` abrufen

  let printTime = printer?.PrintTimeSeconds || 0; // 🔥 Druckzeit aus Printer holen

  let qualityData;
  if (type === "PaperStorageSize") {
    qualityData = getPaperStorageSizeRating(
      Number(item),
      Number(printTime),
      guestCount,
      t,
    );
  } else {
    const rawData = QUALITY_MAP[type]?.[item];
    if (rawData) {
      qualityData = formatRating(
        rawData.ratingKey,
        t(rawData.detailsKey),
        IconThumbUp,
        "text-[#6FA68F]",
        t,
      );
    }
  }

  if (!qualityData) return null; // Falls kein passender Wert, nichts rendern

  const IconComponent = qualityData.icon;

  return (
    <div className="flex justify-start items-start gap-1 relative">
      <div className="w-5">
        <IconComponent className={`w-5 ${qualityData.colorClass}`} />
      </div>

      <span className="text-slate-900 leading-[1.4]">
        {type === "CamType"
          ? t("label.cam")
          : type === "LightType"
          ? t("label.light")
          : type === "PaperStorageSize"
          ? t("label.printer")
          : ""}{" "}
        <span
          className="border-b border-dotted border-[#6FA68F] cursor-help relative"
          onMouseEnter={() => setTooltipVisible(true)}
          onMouseLeave={() => setTooltipVisible(false)}
        >
          {qualityData.rating}
          {tooltipVisible && <Tooltip text={qualityData.details} />}
        </span>
      </span>
    </div>
  );
}

export default ProductListElementQuality;
export { getPaperStorageSizeRating, PAPER_STORAGE_LOOKUP };
